import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@geely/ui';

const emailVerificationSchema = z.object({
  email: z.string().email('Email inválido'),
});

type EmailVerificationData = z.infer<typeof emailVerificationSchema>;

interface EmailVerificationDialogProps {
  isOpen: boolean;
  isVerifying: boolean;
  onSubmit: (email: string) => Promise<void>;
  onClose: () => void;
}

export function EmailVerificationDialog({
  isOpen,
  isVerifying,
  onSubmit,
  onClose,
}: EmailVerificationDialogProps) {
  const form = useForm<EmailVerificationData>({
    resolver: zodResolver(emailVerificationSchema),
    defaultValues: { email: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({ email: '' });
    }
  }, [isOpen, form]);

  const handleSubmit = async (data: EmailVerificationData) => {
    await onSubmit(data.email);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirme sua identidade</DialogTitle>
          <DialogDescription>
            Para modificar este agendamento, informe o email utilizado no momento da reserva.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="py-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="seu@email.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isVerifying}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isVerifying}>
                {isVerifying ? 'Verificando...' : 'Confirmar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
